import { useEffect, useState, useCallback } from "react";
import { api, timeOf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Photo } from "@/components/Photo";
import { CaretLeft, CaretRight, Images } from "@phosphor-icons/react";

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const label = (d) => d.toLocaleDateString("en-US", { month: "long", year: "numeric" });

export default function Album() {
  const [cursor, setCursor] = useState(new Date());
  const [kids, setKids] = useState([]);
  const [sel, setSel] = useState("all");
  const [photos, setPhotos] = useState([]);
  const [zoom, setZoom] = useState(null);

  useEffect(() => { api.get("/students").then(({ data }) => setKids(data)); }, []);

  const load = useCallback(async () => {
    const params = { month: monthKey(cursor), ...(sel !== "all" ? { student_id: sel } : {}) };
    const { data } = await api.get("/photos", { params });
    setPhotos(data);
  }, [cursor, sel]);

  useEffect(() => { load(); }, [load]);

  const shift = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));

  const byDay = photos.reduce((acc, p) => {
    (acc[p.date] = acc[p.date] || []).push(p);
    return acc;
  }, {});

  return (
    <div data-testid="album-page">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Photo Album</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Moments from the month</h1>
      <p className="mt-3 text-muted-foreground">Every photo your child's teachers have shared, newest first.</p>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => shift(-1)} className="rounded-full"
                data-testid="album-prev-btn"><CaretLeft size={18} /></Button>
        <p className="font-display text-2xl font-semibold w-56 text-center" data-testid="album-month-label">{label(cursor)}</p>
        <Button variant="outline" size="icon" onClick={() => shift(1)} className="rounded-full"
                data-testid="album-next-btn"><CaretRight size={18} /></Button>
        <div className="w-56">
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="h-11 rounded-xl bg-white" data-testid="album-child-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All children</SelectItem>
              {kids.map((k) => <SelectItem key={k.id} value={k.id}>{k.first_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <span className="text-sm font-bold text-muted-foreground" data-testid="album-count">
          {photos.length} photo{photos.length === 1 ? "" : "s"}
        </span>
      </div>

      {photos.length === 0 ? (
        <div className="mt-10 bg-white rounded-3xl border border-border p-12 text-center" data-testid="album-empty">
          <Images size={56} weight="duotone" className="mx-auto text-[#3B82F6]" />
          <p className="mt-6 font-display text-xl font-semibold">No photos this month yet</p>
          <p className="mt-2 text-muted-foreground">
            As soon as a teacher shares a photo it will show up right here.
          </p>
        </div>
      ) : (
        <div className="mt-10 space-y-10">
          {Object.entries(byDay).map(([day, list]) => (
            <div key={day} data-testid={`album-day-${day}`}>
              <h2 className="font-display text-xl font-semibold">
                {new Date(`${day}T12:00:00`).toLocaleDateString("en-US",
                  { weekday: "long", month: "long", day: "numeric" })}
              </h2>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {list.map((p) => (
                  <button key={p.id} onClick={() => setZoom(p)}
                          className="text-left rounded-2xl overflow-hidden bg-white border border-border hover:-translate-y-1 hover:shadow-md"
                          style={{ transition: "transform .25s ease, box-shadow .25s ease" }}
                          data-testid={`album-photo-${p.id}`}>
                    <Photo path={p.photo_path} alt={p.detail || "Classroom photo"}
                           className="w-full h-44 object-cover" testid={`album-img-${p.id}`} />
                    <div className="p-4">
                      <p className="text-sm font-bold truncate">{p.student_name}</p>
                      {p.detail && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.detail}</p>}
                      <p className="text-xs text-muted-foreground mt-2">{timeOf(p.at)}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="rounded-3xl max-w-3xl" data-testid="album-lightbox">
          <DialogHeader className="sr-only">
            <DialogTitle>{zoom ? `Photo of ${zoom.student_name}` : "Photo"}</DialogTitle>
            <DialogDescription>{zoom?.detail || "Classroom photo shared by a teacher"}</DialogDescription>
          </DialogHeader>
          {zoom && (
            <div>
              <Photo path={zoom.photo_path} alt={zoom.detail || "Classroom photo"}
                     className="w-full max-h-[65vh] object-contain rounded-2xl bg-muted"
                     testid="album-lightbox-img" />
              <p className="mt-4 font-display text-lg font-semibold">{zoom.student_name}</p>
              {zoom.detail && <p className="mt-1 text-base text-muted-foreground">{zoom.detail}</p>}
              <p className="mt-2 text-sm text-muted-foreground">
                {new Date(`${zoom.date}T12:00:00`).toLocaleDateString("en-US",
                  { weekday: "long", month: "long", day: "numeric" })} · {timeOf(zoom.at)} · {zoom.staff}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
